import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoostActionItem } from './BoostActionItem';
import { BoostedProfileCircle } from './BoostedProfileCircle';
import { ActivateBoostDialog } from './ActivateBoostDialog';
import { useActiveBoostedProfiles } from '@/hooks/useActiveBoostedProfiles';
import { useBoosts } from '@/hooks/useBoosts';
import { useGems } from '@/hooks/useGems';
import { useProfile } from '@/contexts/ProfileContext';
import { getPlanFeatures } from '@/constants/plans';
import { debugLogger } from '@/utils/debugLogger';

interface BoostStoryBarProps {
  isProfileComplete: boolean;
  isVerified: boolean;
}

export const BoostStoryBar: React.FC<BoostStoryBarProps> = ({
  isProfileComplete,
  isVerified
}) => {
  const navigate = useNavigate();
  const [showBoostDialog, setShowBoostDialog] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const { boostedProfiles, hasActiveBoost, refresh } = useActiveBoostedProfiles();
  const { availableBoosts, useBoost, buyAndUseBoost } = useBoosts();
  const { balance: gemBalance } = useGems();
  const { profile } = useProfile();
  
  const planFeatures = getPlanFeatures(profile?.plan_type || 'sponsor_free');

  // Placeholders quando não há perfis com boost
  const placeholders = useMemo(() => {
    const placeholderCount = Math.max(0, 4 - boostedProfiles.length);
    return Array.from({ length: placeholderCount }, (_, i) => ({
      id: `placeholder-${i}`,
      isPlaceholder: true
    }));
  }, [boostedProfiles.length]);

  const handleActivateBoost = async () => {
    setIsActivating(true);
    try {
      const success = await useBoost('profile_highlight');
      if (success) {
        await refresh();
        setShowBoostDialog(false);
      }
    } catch (error) {
      debugLogger.error('Boosts', 'Error activating boost:', error);
    } finally {
      setIsActivating(false);
    }
  };

  const handleBuyAndActivateBoost = async () => {
    setIsActivating(true);
    try {
      const success = await buyAndUseBoost();
      if (success) {
        await refresh();
        setShowBoostDialog(false);
      }
    } catch (error) {
      debugLogger.error('Boosts', 'Error buying and activating boost:', error);
    } finally {
      setIsActivating(false);
    }
  };

  const handleProfileClick = (username: string) => {
    navigate(`/main/user/${username}`);
  };

  // Definir quais action items mostrar baseado no estado do perfil
  const actionItems = useMemo(() => {
    const items: Array<{ type: 'incomplete' | 'unverified' | 'boost'; show: boolean }> = [];

    // 1) Perfil incompleto
    if (!isProfileComplete) {
      items.push({ type: 'incomplete', show: true });
    }

    // 2) Perfil não verificado
    if (!isVerified) {
      items.push({ type: 'unverified', show: true });
    }

    // 3) Sempre mostrar botão de boost
    items.push({ type: 'boost', show: true });

    return items.filter(item => item.show);
  }, [isProfileComplete, isVerified]);

  const handleActionClick = (type: 'incomplete' | 'unverified' | 'boost') => {
    switch (type) {
      case 'incomplete':
        navigate('/main/complete-profile');
        break;
      case 'unverified':
        navigate('/main/verify');
        break;
      case 'boost':
        setShowBoostDialog(true);
        break;
    }
  };

  return (
    <>
      <div className="w-full overflow-x-auto scrollbar-none">
        <div className="flex items-start gap-3 px-4 py-3 min-w-max">
          {/* Action Items (Completar, Verificar, Boost) */}
          {actionItems.map((item) => (
            <div key={item.type} className="relative">
              <BoostActionItem
                type={item.type}
                onClick={() => handleActionClick(item.type)}
                hasActiveBoost={hasActiveBoost}
                availableBoosts={availableBoosts}
              />
            </div>
          ))}

          {/* Separator */}
          {actionItems.length > 0 && (boostedProfiles.length > 0 || placeholders.length > 0) && (
            <div className="w-px h-14 bg-white/10 self-center" />
          )}

          {/* Boosted Profiles */}
          {boostedProfiles.map((profile) => (
            <BoostedProfileCircle
              key={profile.user_id}
              userId={profile.user_id}
              username={profile.username}
              photoUrl={profile.main_photo_url}
              onClick={() => handleProfileClick(profile.username)}
            />
          ))}

          {/* Placeholders */}
          {placeholders.map((placeholder) => (
            <BoostedProfileCircle
              key={placeholder.id}
              userId={placeholder.id}
              username=""
              photoUrl={null}
              onClick={() => {}}
              isPlaceholder
            />
          ))}
        </div>
      </div>

      {/* Dialog de confirmação */}
      <ActivateBoostDialog
        open={showBoostDialog}
        onOpenChange={setShowBoostDialog}
        onConfirm={handleActivateBoost}
        onBuyAndActivate={handleBuyAndActivateBoost}
        availableBoosts={availableBoosts}
        gemBalance={gemBalance}
        canBuyBoosts={planFeatures.can_buy_boosts}
        isLoading={isActivating}
      />
    </>
  );
};
